import { Row } from 'antd';
import { keyBy } from 'lodash';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ProjectId } from 'api/models';
import { Project, ProjectDetailTabKeys } from 'api/project';
import { BreadCrumbs } from 'components/molecules/project-details/header/breadcrumbs';
import { ProjectTitle } from 'components/molecules/project-details/header/project-title';
import { ProjectDetailTabs } from 'components/molecules/project-details/header/tabs/tabs';
import { useProjectTasksRefresh } from 'components/pages/use-project-tasks-refresh';
import { useProjectTasksSocketUpdates } from 'components/pages/use-project-tasks-socket-updates';
import { ProjectDetailsPageTemplate } from 'components/templates/project-details';
import { RootState } from 'data';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { PrereleaseFeature, useFeature } from 'hooks/use-feature';
import { useProjectWebsocket } from 'hooks/websockets';
import { FieldsStore, TaskField } from 'stores/fields';
import { CardNames, data as originalData } from './api';
import {
  CardNameToComponentMapping,
  CustomisableLeftSectionCards,
  HighlightSection,
} from './left-section';
import { LeftArea, OverviewWrapper, RightArea } from './overview.styles';
import { RightSectionCards } from './right-section-cards';

export const ProjectOverviewPage: FC<{
  project: Project;
}> = ({ project }) => {
  const { projectId, projectName } = project;
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const taskFields = useMemo(
    () =>
      keyBy(FieldsStore.enabledTaskFields(fieldsStore), 'fieldName') as Record<
        string,
        TaskField
      >,
    [fieldsStore],
  );
  const cardConfig = CardNameToComponentMapping(projectId, taskFields);

  useProjectTasksRefresh({ projectId: ProjectId(projectId) });
  useProjectTasksSocketUpdates(ProjectId(projectId));
  useProjectWebsocket(ProjectId(projectId));

  const showInferredProgress = useFeature(
    PrereleaseFeature.InferredProgress,
    true,
  );

  const data = useMemo(
    () =>
      showInferredProgress
        ? originalData
        : originalData.filter(
            ({ cardName }) => cardName !== CardNames.PlannedVsActual,
          ),
    [showInferredProgress],
  );
  const { isAllowedForCurrentUser } = usePermissionForRule();

  const projectOverviewCards = useMemo(
    () =>
      data.map((datum) => {
        if (
          datum.ruleName != null &&
          !isAllowedForCurrentUser(datum.ruleName)
        ) {
          return {
            ...datum,
            hidden: true,
          };
        }

        return datum;
      }),
    [data, isAllowedForCurrentUser],
  );

  return (
    <ProjectDetailsPageTemplate
      title={<ProjectTitle project={project} />}
      documentTitle={projectName}
      tabs={
        <ProjectDetailTabs
          projectId={ProjectId(projectId)}
          activeTabKey={ProjectDetailTabKeys.Overview}
        />
      }
      breadCrumbs={<BreadCrumbs />}
    >
      <OverviewWrapper>
        <LeftArea>
          <Row gutter={[16, 16]}>
            <HighlightSection projectId={projectId} />
            <CustomisableLeftSectionCards
              data={projectOverviewCards}
              cardConfig={cardConfig}
            />
          </Row>
        </LeftArea>
        <RightArea>
          <RightSectionCards projectId={projectId} />
        </RightArea>
      </OverviewWrapper>
    </ProjectDetailsPageTemplate>
  );
};
